import { showUndoToast, showToast } from "@/app/components/ui/Toast";
import { postUndoCorrection } from "@/lib/moneyOperations";
import {
  clearLatestMoneyUndo,
  rememberLatestMoneyUndo,
} from "@/lib/latestMoneyUndo";
import type { ReceivableListItem } from "@/lib/pendingOrders";

export function pickMoneyUndoSourceLabel(
  item: ReceivableListItem | null | undefined,
): string {
  if (!item) return "";
  const fields = item.fields ?? [];
  for (const key of [
    "customer_ref",
    "customer_name",
    "addr_name",
    "slip_sender",
    "recipient_name",
  ]) {
    const value = fields.find((field) => field.key === key)?.value?.trim();
    if (value && value !== "—") return value;
  }
  return item.title?.trim() || "";
}

/** Match backend history money formatting (no ฿, trim trailing zeros). */
export function formatMoneyUndoAmount(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded)
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0+$/, "");
}

/**
 * History-style allocate/resplit/match line.
 * - default: `จับคู่ยอดค้าง 509 บาท → ชื่อ 100 · ชื่อ 299`
 * - with from: `จับคู่ยอดค้าง 10 บาทจาก สลิปผู้โอน → พี่เขียว 10`
 * - with to:   `จับคู่ยอดค้าง 10 บาทจาก สลิปA 10 → พี่เขียว`
 */
export function formatAllocateUndoLabel(
  parts: Array<{ label: string; amount: number }>,
  verb: string,
  emptyLabel = "ธุรกรรมล่าสุด",
  options?: { fromLabel?: string | null; toLabel?: string | null },
): string {
  const rows = parts.filter((p) => p.amount > 0.009 && p.label.trim());
  if (rows.length === 0) return emptyLabel;
  const total = rows.reduce((sum, p) => sum + p.amount, 0);
  const breakdown = rows
    .map((p) => `${p.label.trim()} ${formatMoneyUndoAmount(p.amount)}`)
    .join(" · ");
  const verbText = verb.trim() || "จับคู่ยอดค้าง";
  const totalText = formatMoneyUndoAmount(total);
  const from = options?.fromLabel?.trim();
  const to = options?.toLabel?.trim();

  if (from && !to) {
    return `${verbText} ${totalText} บาทจาก ${from} → ${breakdown}`;
  }
  if (to && !from) {
    return `${verbText} ${totalText} บาทจาก ${breakdown} → ${to}`;
  }
  if (from && to) {
    return `${verbText} ${totalText} บาทจาก ${from} → ${to}`;
  }
  return `${verbText} ${totalText} บาท → ${breakdown}`;
}

export function formatMoneyUndoLabel(
  action: string,
  sourceLabel?: string | null,
): string {
  const actionText = action.trim() || "ธุรกรรมล่าสุด";
  const source = sourceLabel?.trim();
  if (!source) return actionText;
  return `${actionText} · จาก ${source}`;
}

export function offerMoneyUndoToast(args: {
  message: string;
  sourceLabel?: string | null;
  ownerUserId: string;
  workspaceId: string;
  operationId: string | null | undefined;
  onUndone?: () => void | Promise<void>;
}) {
  const label = formatMoneyUndoLabel(args.message, args.sourceLabel);
  const opId = args.operationId?.trim();
  if (!opId || !args.ownerUserId.trim() || !args.workspaceId.trim()) {
    showToast(label);
    return;
  }

  rememberLatestMoneyUndo({
    operationId: opId,
    ownerUserId: args.ownerUserId,
    workspaceId: args.workspaceId,
    label,
    savedAt: Date.now(),
  });

  showUndoToast(label, async () => {
    try {
      await postUndoCorrection({
        ownerUserId: args.ownerUserId,
        workspaceId: args.workspaceId,
        targetOperationId: opId,
      });
      clearLatestMoneyUndo();
      showToast("ย้อนกลับสำเร็จ");
      await args.onUndone?.();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "ย้อนกลับไม่สำเร็จ",
        "error",
      );
    }
  });
}
